import { create } from 'zustand';
import { ChatMessage, ChatSession, ChatSettings, MCPServer, MCPTool, MCPServerMetadata } from '../types';
import { apiService } from '../services/api';

interface ChatStore {
  // Current session
  currentSession: ChatSession | null;
  isStreaming: boolean;
  isProcessingQueue: boolean;
  error: string | null;
  messageQueue: Array<{ content: string; files?: File[] }>;

  // Settings
  settings: ChatSettings;
  
  // MCP data
  mcpServers: MCPServer[];
  mcpTools: MCPTool[];
  mcpMetadata: MCPServerMetadata[];
  
  // Actions
  createSession: (contextPrompt?: string, sendHello?: boolean) => void;
  restoreSession: (sessionData: { sessionId: string; messages: ChatMessage[]; settings?: ChatSettings }) => void;
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp' | 'status'>) => void;
  updateLastMessage: (content: string) => void;
  updateMessageStatus: (messageId: string, status: ChatMessage['status']) => void;
  sendMessage: (content: string, files?: File[]) => Promise<void>;
  sendMessageInternal: (content: string, files?: File[], fromQueue?: boolean) => Promise<void>;
  queueMessage: (content: string, files?: File[]) => void;
  processQueue: () => Promise<void>;
  updateSettings: (settings: Partial<ChatSettings>) => void;
  setStreaming: (streaming: boolean) => void;
  setError: (error: string | null) => void;
  
  // MCP actions
  loadMCPData: () => Promise<void>;
}

const defaultSettings: ChatSettings = {
  provider: 'openai',
  model: 'gpt-4o-mini',
  temperature: 0.7,
  maxTokens: 4096,
  mcpServers: [],
  domain: 'general',
  costSensitivity: 'medium',
  dataSensitivity: 'internal',
};

export const useChatStore = create<ChatStore>((set, get) => ({
  currentSession: null,
  isStreaming: false,
  isProcessingQueue: false,
  error: null,
  messageQueue: [],
  settings: defaultSettings,
  mcpServers: [],
  mcpTools: [],
  mcpMetadata: [],

  createSession: (contextPrompt?: string, sendHello?: boolean) => {
    const { settings, sendMessage } = get();
    const session: ChatSession = {
      id: crypto.randomUUID(),
      workspaceId: settings.workspaceId || 'default',
      title: 'New Chat',
      messages: contextPrompt ? [{
        id: crypto.randomUUID(),
        role: 'system',
        content: contextPrompt,
        timestamp: new Date(),
      }] : [],
      metadata: {
        contextPrompt,
        provider: settings.provider,
        model: settings.model,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    set({ currentSession: session, error: null });
    
    // Send Hello message to trigger LLM introduction if requested
    if (sendHello) {
      // Use setTimeout to ensure session is fully set before sending message
      setTimeout(() => {
        sendMessage('Hello');
      }, 100);
    }
  },

  addMessage: (message) => {
    const { currentSession } = get();
    if (!currentSession) return;

    const newMessage: ChatMessage = {
      id: crypto.randomUUID(),
      timestamp: new Date(),
      ...message,
    };

    const updatedMessages = [...currentSession.messages, newMessage];
    const updatedSession = {
      ...currentSession,
      messages: updatedMessages,
      updatedAt: new Date(),
      title: updatedMessages.length === 1 ? 
        message.content.slice(0, 50) + '...' : 
        currentSession.title,
    };

    set({ currentSession: updatedSession });
  },

  updateLastMessage: (content) => {
    // Debug: Check if content is a string
    if (typeof content !== 'string') {
      console.error('❌ updateLastMessage received non-string content:', typeof content, content);
      console.error('Content value:', content);
      console.error('Stack trace:', new Error().stack);
      
      // Try to convert to string
      if (content && typeof content === 'object') {
        if (typeof content.toString === 'function') {
          content = content.toString();
          console.log('✅ Converted object to string:', content);
        } else {
          console.error('Cannot convert object to string - skipping update');
          return;
        }
      } else {
        content = String(content || '');
      }
    }

    const { currentSession } = get();
    if (!currentSession || currentSession.messages.length === 0) return;

    const messages = [...currentSession.messages];
    const lastMessage = messages[messages.length - 1];
    
    if (lastMessage.role === 'assistant') {
      lastMessage.content = content;
      set({
        currentSession: {
          ...currentSession,
          messages,
          updatedAt: new Date(),
        },
      });
    }
  },

  updateMessageStatus: (messageId, status) => {
    const { currentSession } = get();
    if (!currentSession) return;

    const messages = [...currentSession.messages];
    const messageIndex = messages.findIndex(msg => msg.id === messageId);
    
    if (messageIndex >= 0) {
      messages[messageIndex] = { ...messages[messageIndex], status };
      set({
        currentSession: {
          ...currentSession,
          messages,
          updatedAt: new Date(),
        },
      });
    }
  },

  queueMessage: (content, files) => {
    const { messageQueue, isStreaming, sendMessage } = get();
    
    if (!isStreaming) {
      // If not streaming, send immediately
      sendMessage(content, files);
      return;
    }

    // Add queued message to chat display
    get().addMessage({ 
      role: 'user', 
      content: content + (files?.length ? `\n\nAttached files: ${files.map(f => f.name).join(', ')}` : ''),
      status: 'queued'
    });

    // Add to processing queue
    set({ 
      messageQueue: [...messageQueue, { content, files }] 
    });
  },

  processQueue: async () => {
    const { messageQueue, isStreaming, isProcessingQueue } = get();
    
    // Don't process queue if still streaming, already processing, or no messages to process
    if (isStreaming || isProcessingQueue || messageQueue.length === 0) {
      return;
    }
    
    // Set processing flag to prevent concurrent processing
    set({ isProcessingQueue: true });
    
    // Double-check streaming state after a small delay to ensure it's really finished
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const currentState = get();
    if (currentState.isStreaming) {
      // Still streaming, clear flag and try again later
      set({ isProcessingQueue: false });
      setTimeout(() => {
        get().processQueue();
      }, 500);
      return;
    }

    // Process next message in queue
    const nextMessage = messageQueue[0];
    const remainingQueue = messageQueue.slice(1);
    
    // Update queue and mark queued message as processing
    set({ messageQueue: remainingQueue });
    
    // Find the queued message in the chat and update its status to processing
    const stateForUpdate = get();
    if (stateForUpdate.currentSession) {
      const messages = [...stateForUpdate.currentSession.messages];
      const queuedMessageIndex = messages.findIndex(msg => 
        msg.role === 'user' && 
        msg.status === 'queued' && 
        msg.content.includes(nextMessage.content)
      );
      
      if (queuedMessageIndex >= 0) {
        messages[queuedMessageIndex] = { ...messages[queuedMessageIndex], status: 'processing' };
        set({
          currentSession: {
            ...stateForUpdate.currentSession,
            messages,
            updatedAt: new Date(),
          }
        });
      }
    }
    
    try {
      // Send the message (use internal send that doesn't add duplicate user message)
      await get().sendMessageInternal(nextMessage.content, nextMessage.files, true);
    } catch (error) {
      console.error('Error processing queued message:', error);
    } finally {
      // Clear processing flag
      set({ isProcessingQueue: false });
    }
    
    // Process next message if queue not empty
    if (remainingQueue.length > 0) {
      // Wait a bit then process next
      setTimeout(() => {
        get().processQueue();
      }, 100);
    }
  },

  sendMessage: async (content, files) => {
    return get().sendMessageInternal(content, files, false);
  },

  sendMessageInternal: async (content, files, fromQueue = false) => {
    const { currentSession, settings, addMessage, setStreaming, setError } = get();
    
    if (!currentSession) {
      get().createSession();
    }

    try {
      setError(null);
      setStreaming(true);

      let displayContent = content;
      let llmContent = content;

      // Handle file uploads if provided
      if (files && files.length > 0) {
        try {
          const uploadResult = await apiService.uploadFiles(
            settings.workspaceId || 'default',
            files,
            content
          );

          // Create separate content for display vs LLM
          const fileDisplayItems = uploadResult.files.map(file => 
            file.isBinary ? `📎 ${file.name}` : `📄 ${file.name}`
          );

          // For display: show file icons/names
          displayContent = content + 
            `\n\nAttached files (${uploadResult.totalFiles}): ${fileDisplayItems.join(', ')}`;

          // For LLM: include full file contents
          const fileContentsForLLM = uploadResult.files
            .map(file => `\n\n--- File: ${file.name} ---\n${file.content}`)
            .join('');

          llmContent = content + 
            `\n\nI've attached ${uploadResult.totalFiles} file(s) with a total size of ${(uploadResult.totalSize / 1024).toFixed(1)}KB:` +
            fileContentsForLLM;

        } catch (error) {
          console.error('File upload failed:', error);
          setError(error instanceof Error ? error.message : 'File upload failed');
          setStreaming(false);
          return;
        }
      }

      // Add user message (display version with file icons) - skip if from queue since it's already there
      if (!fromQueue) {
        addMessage({ role: 'user', content: displayContent });
      } else {
        // If from queue, update the queued message to show it's now processing
        const currentState = get();
        if (currentState.currentSession) {
          const messages = [...currentState.currentSession.messages];
          const queuedMessageIndex = messages.findIndex(msg => 
            msg.role === 'user' && 
            (msg.status === 'queued' || msg.status === 'processing') &&
            msg.content.includes(content)
          );
          
          if (queuedMessageIndex >= 0) {
            messages[queuedMessageIndex] = { 
              ...messages[queuedMessageIndex], 
              content: displayContent, // Update with file attachments if any
              status: 'processing' 
            };
            set({
              currentSession: {
                ...currentState.currentSession,
                messages,
                updatedAt: new Date(),
              }
            });
          }
        }
      }

      // Add assistant message placeholder
      addMessage({ role: 'assistant', content: '' });

      const messages = get().currentSession?.messages || [];
      
      // Remove the last message (empty assistant placeholder) and filter ALL empty assistant messages
      const beforeFilter = messages.slice(0, -1);
      
      // Create modified messages for LLM with full content for the last user message
      const messagesToSend = beforeFilter.filter(message => {
        // Filter out any empty assistant messages from the conversation history
        const isEmpty = message.role === 'assistant' && !message.content && !message.toolCalls?.length && !message.toolResults?.length;
        if (isEmpty) {
          return false;
        }
        return true;
      }).map((message, index, array) => {
        // Replace the last user message content with the LLM version (includes full file content)
        if (index === array.length - 1 && message.role === 'user' && llmContent !== displayContent) {
          return { ...message, content: llmContent };
        }
        return message;
      });
      
      
      
      
      let assistantContent = '';

      // Check if we have filtered out any empty assistant messages and force session reset if so
      const hasFilteredEmptyMessages = beforeFilter.length > messagesToSend.length;
      
      // Check if this involves shared mailbox operations and show progress
      const isSharedMailboxQuery = content.toLowerCase().includes('shared') || 
                                   content.toLowerCase().includes('mailbox') ||
                                   content.includes('@');
      
      if (isSharedMailboxQuery) {
        // Show initial progress message
        get().updateLastMessage('🔍 Accessing mailbox...');
      }

      // Stream response
      const stream = apiService.streamChat(
        settings.workspaceId || 'default',
        messagesToSend, 
        {
          mcpServers: settings.mcpServers
            .map(server => server?.name)
            .filter(name => name && typeof name === 'string' && name.trim().length > 0),
          routeHint: {
            domain: settings.domain,
            costSensitivity: settings.costSensitivity,
            dataSensitivity: settings.dataSensitivity,
          },
          aiName: settings.aiName, // Pass the AI name from settings
          forceNewSession: hasFilteredEmptyMessages, // Force new session if we filtered problematic messages
        }
      );

      for await (const chunk of stream) {
        if (chunk.type === 'text' && chunk.delta) {
          // Debug: Check if chunk.delta is actually a string
          if (typeof chunk.delta !== 'string') {
            console.error('❌ Streaming chunk.delta is not a string:', typeof chunk.delta, chunk.delta);
            console.error('Full chunk:', chunk);
            continue; // Skip this chunk
          }
          
          assistantContent += chunk.delta;
          
          // Debug: Check if accumulated content is still a string
          if (typeof assistantContent !== 'string') {
            console.error('❌ Accumulated assistantContent is not a string:', typeof assistantContent, assistantContent);
            assistantContent = String(assistantContent || '');
          }
          
          get().updateLastMessage(assistantContent);
        } else if (chunk.type === 'tool_call' && chunk.call) {
          // Handle tool calls - could show in UI
          // Tool call in progress
        } else if (chunk.type === 'final') {
          // Streaming is complete - stop the indicator
          setStreaming(false);
          
          // Mark user message as completed if it was processing
          if (fromQueue) {
            const currentState = get();
            if (currentState.currentSession) {
              const messages = [...currentState.currentSession.messages];
              const processingMessageIndex = messages.findIndex(msg => 
                msg.role === 'user' && 
                msg.status === 'processing' &&
                msg.content.includes(content)
              );
              
              if (processingMessageIndex >= 0) {
                messages[processingMessageIndex] = { 
                  ...messages[processingMessageIndex], 
                  status: 'completed' 
                };
                set({
                  currentSession: {
                    ...currentState.currentSession,
                    messages,
                    updatedAt: new Date(),
                  }
                });
              }
            }
          }
          
          if (chunk.usage) {
            // Token usage available
          }
          if (chunk.routing) {
            // Routing decision made
          }
          // Process any queued messages
          setTimeout(() => {
            get().processQueue();
          }, 1000);
        } else if (chunk.type === 'error') {
          throw new Error(chunk.message || 'Unknown streaming error');
        }
      }

    } catch (error) {
      console.error('Send message failed:', error);
      
      // Handle authentication errors specifically
      if (error instanceof Error && error.message.includes('Not authenticated')) {
        setError('Please sign in to continue chatting');
        // Optionally trigger a re-authentication check
        window.location.reload();
      } else {
        setError(error instanceof Error ? error.message : 'Failed to send message');
      }
      
      setStreaming(false);
      // Process any queued messages even after error
      setTimeout(() => {
        get().processQueue();
      }, 1000);
    }
  },

  updateSettings: (newSettings) => {
    set({
      settings: {
        ...get().settings,
        ...newSettings,
      },
    });
  },

  setStreaming: (isStreaming) => {
    set({ isStreaming });
  },

  setError: (error) => {
    set({ error });
  },

  restoreSession: (sessionData) => {
    const { settings, isStreaming } = get();
    
    // Convert timestamp strings back to Date objects
    const restoredMessages = sessionData.messages.map((message: ChatMessage) => ({
      ...message,
      timestamp: new Date(message.timestamp),
    }));
    
    
    const restoredSession: ChatSession = {
      id: sessionData.sessionId,
      workspaceId: settings.workspaceId || 'default',
      title: restoredMessages.length > 0 ? 
        restoredMessages[0].content.slice(0, 50) + '...' :
        'Restored Chat',
      messages: restoredMessages,
      metadata: {
        provider: settings.provider,
        model: settings.model,
        ...sessionData.settings,
      },
      createdAt: new Date(sessionData.sessionId), // Use sessionId timestamp if available
      updatedAt: new Date(),
    };
    
    
    // Apply any restored settings but preserve streaming state if currently streaming
    if (sessionData.settings) {
      set({
        currentSession: restoredSession,
        settings: { ...settings, ...sessionData.settings },
        isStreaming: isStreaming, // Preserve current streaming state
        error: null
      });
    } else {
      set({ 
        currentSession: restoredSession, 
        isStreaming: isStreaming, // Preserve current streaming state
        error: null 
      });
    }
    
    // Verify the state was set correctly
    const newState = get();
  },

  loadMCPData: async () => {
    try {
      const [servers, tools, metadata] = await Promise.all([
        apiService.getMCPServers(),
        apiService.getMCPTools(),
        apiService.getMCPMetadata(),
      ]);
      
      set({ 
        mcpServers: servers || [], 
        mcpTools: tools || [], 
        mcpMetadata: metadata || [] 
      });
    } catch (error) {
      console.error('Failed to load MCP data:', error);
      // Set empty arrays instead of error to show "no servers/tools" state
      set({ mcpServers: [], mcpTools: [], mcpMetadata: [] });
    }
  },
}));